import { ModuleRegistry } from "ag-grid-community";
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MultiFilterModule,
  PivotModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  SetFilterModule,
  SideBarModule,
  TreeDataModule,
} from "ag-grid-enterprise";
import { forwardRef, useCallback, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import { columnTypesBoolean } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesBoolean";
import { columnTypesDate } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import AgGridTableWrapper from "../../../agGrid/AgGridTableWrapper";
import { isBoolean, isFunction, isNil, size } from "lodash";
import Flx from "../../../layout/Flx";
import Txt from "../../../typography/Txt";
import { useSelector } from "react-redux";
import { Badge, Box, Checkbox, Chip, Tooltip } from "@mui/material";
import {
  ChatBubbleOutline,
  LocalOfferOutlined,
  OutlinedFlagOutlined,
  ReportProblemOutlined,
  SpeakerNotesOffOutlined,
} from "@mui/icons-material";
import { amber, blue, brown, green, red } from "@mui/material/colors";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { teal } from "../../../../theme/colors";
import { getUserFullNameFromEmail } from "../../../../utils/functions/getUserFullNameFromEmail";
import ChipItem from "../../../ui/ChipItem";
import { getDaysAgo } from "../../../../_src_shared/utils/functions/getDaysAgo";
import CommentThreadPreviewTooltip from "../../../tooltips/CommentThreadPreviewTooltip";
import { formatDateShort } from "../../../../utils/dates/formatDateShort";

// Slate-leaning neutral palette mirrored from the Exceptions redesign tab so the
// Status / Risk Bucket / Assigned cells render identically across both tables.
const panel = {
  surface: "#ffffff",
  border: "#e2e8f0",
  borderSubtle: "#eef2f7",
  text: "#0f172a",
  textStrong: "#1e293b",
  textBody: "#334155",
  textLabel: "#64748b",
  textFaint: "#94a3b8",
  avatarBg: "#eef2ff",
  avatarFg: "#4f46e5",
};

const STATUS_TONE = {
  Open: { fg: panel.textBody, bg: panel.borderSubtle, bar: panel.textFaint },
  "In Review": { fg: amber[900], bg: amber[50], bar: amber[500] },
  "Pending Approval": { fg: amber[900], bg: amber[50], bar: amber[500] },
  Approved: { fg: green[800], bg: green[50], bar: green[500] },
  Rejected: { fg: red[800], bg: red[50], bar: red[500] },
  Resolved: { fg: green[800], bg: green[50], bar: green[500] },
};

const getStatusTone = (status) =>
  STATUS_TONE[status] || {
    fg: panel.textBody,
    bg: panel.borderSubtle,
    bar: panel.textFaint,
  };

ModuleRegistry.registerModules([
  SideBarModule,
  RowGroupingPanelModule,
  MultiFilterModule,
  RowGroupingModule,
  PivotModule,
  TreeDataModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  SetFilterModule,
]);

const LoanExceptionsTable = forwardRef(
  (
    {
      rowData,
      onRowClicked,
      simpleTable,
      selectedId,
      masterDetail,
      quickFilterText,
    },
    ref,
  ) => {
    const exception = useSelector((state) => state?.sidebar?.state);
    const users = useSelector((state) => state.users);

    const columnDefsFull = useMemo(() => {
      return [
        {
          headerName: "Status",
          field: "exceptionStatus",
          cellRenderer: StatusCell,
          valueGetter: (p) => p?.data?.exceptionStatus,
          width: 150,
          minWidth: 140,
          maxWidth: 200,
          flex: 0,
          cellStyle: {
            display: "flex",
            //
            // alignItems: "center",
            alignItems: "flex-start",
          },
          // cellStyle: { paddingTop: "24px", paddingBottom: "16px" },
        },
        {
          headerName: "Exception Type",
          field: "isHardException",
          // Read the explicit flag — the old logic inferred type from the
          // presence of `taskListItemId`, so any exception created outside a
          // checklist task was mislabeled "Hard Exception".
          valueGetter: (params) =>
            params?.data?.isHardException ? "Hard Exception" : "Soft Exception",
          width: 130,
          minWidth: 120,
          maxWidth: 150,
          flex: 0,
        },
        // {
        //   headerName: "Description",
        //   field: "exceptionDescription",
        //   // suppressAutoSize: true,
        //   // suppressSizeToFit: true,
        //   cellStyle: {
        //     lineHeight: 1.6,
        //     wordBreak: "break-word",
        //     overflowWrap: "break-word",
        //     // Preserve textarea formatting (newlines + tabs) so the
        //     // display matches the edit form's layout.
        //     whiteSpace: "pre-wrap",
        //   },
        //   minWidth: 250,
        //   width: 500,
        //   maxWidth: 600,
        // },
        {
          headerName: "Exception Description",
          field: "exceptionDescription",
          cellRenderer: ExceptionSummaryCell,
          flex: 3,
          minWidth: 320,
          maxWidth: 560,
          autoHeight: true,
          wrapText: true,
          cellStyle: {
            overflow: "hidden",
            paddingTop: "14px",

            paddingBottom: "14px",
            lineHeight: "1.45",
          },
        },
        {
          headerName: "Compensating Factors",
          field: "compensatingFactors",
          suppressAutoSize: true,
          suppressSizeToFit: true,
          wrapText: true,
          autoHeight: true,
          flex: 3,
          minWidth: 250,
          // Same type size as the description cell so the two read as a pair.
          cellStyle: {
            fontSize: "11px",
            color: panel.text,
            lineHeight: "1.6",
            paddingTop: "14px",
            paddingBottom: "14px",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "pre-wrap",
          },
        },
        {
          headerName: "Comments",
          field: "comments",
          cellRenderer: ExceptionCommentsCell,
          cellRendererParams: { users },
          // Checklist-sourced rows carry the thread as `conditionComments` —
          // same fallback the old pinned column used.
          valueGetter: (p) =>
            p?.data?.comments || p?.data?.conditionComments || null,
          // Sort on the NEWEST comment. Exception comments are `$push`ed
          // oldest-first (EXCEPTIONS.md §11.3), so comments[0] is the oldest —
          // the opposite of loan comments. Rows with no comments sort last.
          comparator: (a, b) => newestCommentTime(a) - newestCommentTime(b),
          suppressAutoSize: true,
          suppressSizeToFit: true,
          autoHeight: true,
          wrapText: true,
          flex: 3,
          minWidth: 300,
          cellStyle: {
            cursor: "pointer",
            paddingTop: "14px",
            paddingBottom: "14px",
            paddingLeft: "14px",
            lineHeight: "1.45",
          },
        },
        {
          headerName: "Classification",
          colId: "classification",
          field: "riskBucket",
          cellRenderer: ClassificationCell,
          valueGetter: (p) =>
            [p?.data?.riskBucket, p?.data?.exceptionCategory]
              .filter(Boolean)
              .join(" · ") || null,
          width: 200,
          minWidth: 170,
          maxWidth: 240,
          flex: 0,
          autoHeight: true,
          cellStyle: { paddingTop: "12px", paddingBottom: "12px" },
        },
        // Folded into Classification — kept (hidden) so the Edit Columns panel
        // can bring either back on its own.
        {
          headerName: "Risk Bucket",
          field: "riskBucket",
          colId: "riskBucket",
          cellRenderer: RiskBucketCell,
          valueGetter: (p) => p?.data?.riskBucket,
          width: 170,
          minWidth: 120,
          hide: true,
        },
        {
          headerName: "Excpt. Category",
          field: "exceptionCategory",
          colId: "exceptionCategory",
          cellRenderer: ExceptionCategoryCell,
          valueGetter: (p) => p?.data?.exceptionCategory,
          width: 150,
          minWidth: 120,
          maxWidth: 150,
          flex: 0,
          hide: true,
        },
        {
          headerName: "People",
          colId: "people",
          field: "assignedTo",
          cellRenderer: PeopleCell,
          cellRendererParams: { users },
          // Sort/filter on the assignee — the field people actually scan for.
          valueGetter: (p) => p?.data?.assignedTo,
          width: 190,
          minWidth: 170,
          maxWidth: 230,
          flex: 0,
          autoHeight: true,
          cellStyle: { paddingTop: "12px", paddingBottom: "12px" },
        },
        {
          headerName: "Assigned To",
          field: "assignedTo",
          colId: "assignedTo",
          cellRenderer: AssignedCell,
          cellRendererParams: { users },
          valueGetter: (p) => p?.data?.assignedTo,
          width: 130,
          maxWidth: 130,
          flex: 0,
          hide: true,
        },
        {
          headerName: "Created By",
          field: "createdBy",
          colId: "createdBy",
          cellRenderer: CreatedByCell,
          cellRendererParams: { users },
          valueGetter: (p) => p?.data?.createdBy,
          width: 130,
          maxWidth: 130,
          flex: 0,
          hide: true,
        },
        {
          headerName: "Dates",
          colId: "dates",
          field: "updateDateTime",
          cellRenderer: DatesCell,
          // Sort on last-updated — the date that actually moves.
          valueGetter: (p) => p?.data?.updateDateTime || null,
          width: 175,
          minWidth: 160,
          maxWidth: 200,
          flex: 0,
          autoHeight: true,
          cellStyle: { paddingTop: "12px", paddingBottom: "12px" },
        },
        // Folded into Dates — kept (hidden) for the Edit Columns panel.
        {
          headerName: "Excpt. Date",
          field: "exceptionDate",
          colId: "exceptionDate",
          type: "date",
          hide: true,
        },
        {
          headerName: "Resolution Date",
          field: "resolutionDateTime",
          colId: "resolutionDateTime",
          type: "date",
          width: 150,
          maxWidth: 150,
          flex: 0,
          hide: true,
        },

        {
          headerName: "Send Email",
          field: "sendEmail",
          colId: "sendEmail",
          hide: true,
          width: 120,
          maxWidth: 120,
          flex: 0,
          valueGetter: (p) => {
            if (
              isNil(p?.data?.sendEmail) ||
              !p?.data?.sendEmail ||
              !isBoolean(p?.data?.sendEmail)
            ) {
              return false;
            } else {
              return true;
            }
          },
          cellRenderer: (p) => {
            return (
              <Flx sx={{ height: "100%" }}>
                <Checkbox sx={{ p: 0 }} checked={p.value} disabled />
              </Flx>
            );
          },
        },

        {
          headerName: "Task List Item ID",
          field: "taskListItemId",
          hide: true,
        },
        {
          headerName: "Update Date",
          field: "updateDateTime",
          colId: "updateDateTime",
          width: 150,
          maxWidth: 150,
          flex: 0,
          type: "date",
          hide: true,
        },
      ];
    }, [users]);

    const columnDefs = columnDefsFull;


    const handleOnRowClicked = useCallback(
      (params) => {
        if (isFunction(onRowClicked)) onRowClicked(params?.data);
      },
      [onRowClicked],
    );

    const defaultColDef = useMemo(
      () => ({
        filter: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        resizeable: true,
        flex: 1,
        minWidth: 150,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          paddingTop: "16px",
          paddingBottom: "16px",
          lineHeight: "18px",
        },
        valueGetter: (params) => {
          const def = params?.colDef?.field;
          const data = params?.data || {};
          const value = data[def];

          if (value == null || value === "") return "—";
          return value;
        },
      }),
      [],
    );

    const columnTypes = useMemo(
      () => ({
        ...columnTypesDate(),
        ...columnTypesBoolean(),
      }),
      [],
    );

    const getRowStyle = useCallback(
      (params) => {
        if (exception?._id === params?.data?._id) {
          return {
            backgroundColor: "#e3f2fd63",
            fontWeight: 600,
            borderLeft: `3px solid ${blue[500]}`,
          };
        }
      },
      [exception?._id],
    );

    return (
      <>
        <AgGridTableWrapper
          suppressMinHeightWhenPopulated={rowData}
          // height={height}
          // simpleTable
          compactTable
          sx={{ minWidth: "340px" }}
        >
          <AgGridReact
            ref={ref}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            getRowStyle={getRowStyle}
            quickFilterText={quickFilterText}
            columnTypes={columnTypes}
            domLayout={"autoHeight"}
            onRowClicked={handleOnRowClicked}
          />
        </AgGridTableWrapper>
      </>
    );
  },
);

/* ─── Cell renderers (mirrored from the Exceptions redesign tab) ─── */

/**
 * The shared `[ label  value ]` pill used by the combined columns. A quiet grey
 * label carries the field name so the value can be read on its own, which is
 * what lets Risk + Category (and Assigned + Created) share one column without
 * losing which is which.
 */
const LabelChip = ({ label, value, title }) => (
  <Tooltip title={title || ""} arrow disableHoverListener={!title}>
    <Flx
      ac
      g={0.75}
      sx={{
        border: `1px solid ${panel.border}`,
        background: panel.surface,
        borderRadius: "999px",
        px: 1,
        py: 0.3,
        maxWidth: "100%",
        minWidth: 0,
        alignSelf: "flex-start",
      }}
    >
      <Txt sx={{ fontSize: 9.5, color: panel.textLabel, flexShrink: 0 }}>
        {label}
      </Txt>
      <Txt
        sx={{
          fontSize: 10.5,
          fontWeight: 600,
          color: teal[700],
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Txt>
    </Flx>
  </Tooltip>
);

const EmptyDash = () => (
  <Txt sx={{ fontSize: 11, color: panel.textFaint }}>—</Txt>
);

/** Risk Bucket + Exception Category, stacked as label/value pills. */
const ClassificationCell = ({ data }) => {
  const risk = data?.riskBucket;
  const category = data?.exceptionCategory;
  if (!risk && !category) return <EmptyDash />;
  return (
    <Flx column g={0.5} sx={{ minWidth: 0 }}>
      {risk && <LabelChip label="Risk" value={risk} />}
      {category && <LabelChip label="Category" value={category} />}
    </Flx>
  );
};

/** Assigned To + Created By, in the same pill treatment. */
const PeopleCell = ({ data, users }) => {
  const assigned = data?.assignedTo;
  const created = data?.createdBy;
  if (!assigned && !created) return <EmptyDash />;
  return (
    <Flx column g={0.5} sx={{ minWidth: 0 }}>
      {assigned && (
        <LabelChip
          label="Assigned"
          value={getUserFullNameFromEmail(users, assigned)}
          title={assigned}
        />
      )}
      {created && (
        <LabelChip
          label="Created"
          value={getUserFullNameFromEmail(users, created)}
          title={created}
        />
      )}
    </Flx>
  );
};

/**
 * Exception / resolution / last-updated dates in one column: label left, date
 * right, and a row only appears when it has a value.
 */
const DatesCell = ({ data }) => {
  const rows = [
    { label: "Excpt.", value: data?.exceptionDate },
    { label: "Resolved", value: data?.resolutionDateTime },
    { label: "Updated", value: data?.updateDateTime },
  ].filter((r) => !isNil(r.value) && r.value !== "");

  if (!rows.length) return <EmptyDash />;

  return (
    <Flx column g={0.5} sx={{ minWidth: 0 }}>
      {rows.map((r) => (
        <LabelChip
          key={r.label}
          label={r.label}
          value={formatDate(r.value, "—", "M/d/yyyy")}
        />
      ))}
    </Flx>
  );
};

const StatusCell = ({ value, data }) => {
  const status = value || "Open";
  const tone = getStatusTone(status);
  // Resolution date is only meaningful on Approved/Rejected/Resolved — stack it
  // under the chip when present so a separate Resolution column isn't needed.
  const isResolved =
    status === "Approved" || status === "Rejected" || status === "Resolved";
  const resolutionDate =
    isResolved && data?.resolutionDateTime
      ? formatDateShort(data.resolutionDateTime)
      : null;
  return (
    <Flx column sx={{ gap: 0.25 }}>
      <Chip
        size="small"
        label={status}
        icon={
          <Box
            component="span"
            sx={{
              width: 7,
              height: 7,
              flexShrink: 0,
              borderRadius: "50%",
              background: tone.bar,
              // ml: "8px !important",
            }}
          />
        }
        sx={{
          display: "flex",
          // flexWrap: "wrap",
          height: "auto",
          minHeight: 22,
          fontSize: 9,
          fontWeight: 600,
          color: tone.fg,
          background: tone.bg,
          border: `1px solid ${tone.bar}33`,
          alignItems: "center",
          py: 0.25,
          "& .MuiChip-label": {
            whiteSpace: "normal",
            overflow: "visible",
            textOverflow: "clip",
            textAlign: "center",
            lineHeight: 1.25,
          },
        }}
      />
      {resolutionDate && (
        <Flx column center sx={{ mt: 0.5 }}>
          <Txt sx={{ fontSize: 10.5, color: panel.textLabel, lineHeight: 1.3 }}>
            Resolved
          </Txt>
          <Txt sx={{ fontSize: 10.5, color: panel.textLabel, lineHeight: 1.3 }}>
            {resolutionDate}
          </Txt>
        </Flx>
      )}
    </Flx>
  );
};

const ExceptionSummaryCell = ({ data }) => {
  if (!data) return null;
  const isHardException = !!data?.isHardException;
  // The comment count used to sit here as a chip — the Comments column carries
  // the thread now, so the description stays just the description.
  const description =
    data?.exceptionDescription ||
    (isHardException ? "Hard exception" : "Soft exception");

  return (
    <Flx column g={0.25} sx={{ py: 0.25, minWidth: 0, width: "100%" }}>
      {isHardException && (
        <Flx ac g={0.5} wrap sx={{ mb: 0.25 }}>
          {isHardException && (
            <Tooltip
              arrow
              title="A hard exception is severe enough that the deal may not be able to proceed as currently structured, and requires sign-off before moving forward."
            >
              <Chip
                size="small"
                label="Hard Exception"
                icon={
                  <ReportProblemOutlined
                    sx={{
                      fontSize: "9px !important",
                      color: `${red[600]} !important`,
                    }}
                  />
                }
                sx={{
                  height: 16,
                  fontSize: 9,
                  fontWeight: 500,
                  color: red[800],
                  background: red[50],
                  border: `1px solid ${red[100]}`,
                  px: 0.2,
                  cursor: "help",
                }}
              />
            </Tooltip>
          )}
        </Flx>
      )}
      <Txt
        sx={{
          fontSize: 11,
          color: panel.text,
          // lineHeight: 1.45,
          minWidth: 0,
          // Wrap up to 3 lines, ellipsize the rest. `pre-wrap` so source
          // newlines/tabs from the textarea-edited description survive.
          // display: "-webkit-box",
          // WebkitLineClamp: 3,
          // WebkitBoxOrient: "vertical",
          // overflow: "hidden",
          // whiteSpace: "pre-wrap",
          // wordBreak: "break-word",

          lineHeight: 1.6,
          wordBreak: "break-word",
          overflowWrap: "break-word",
          // Preserve textarea formatting (newlines + tabs) so the
          // display matches the edit form's layout.
          whiteSpace: "pre-wrap",
        }}
      >
        {description}
      </Txt>
    </Flx>
  );
};

/**
 * Comments cell, mirroring the pre-close pipeline's `CellRendererLoanComments`
 * (badge + author + date + relative-age chip, then the comment body) with two
 * differences: the newest comment is resolved by timestamp rather than taken at
 * index 0 (exception comments are stored oldest-first), and its text renders in
 * FULL rather than clamped to four lines. Hovering still opens the enhanced
 * thread preview; clicking falls through to the row click, which opens the
 * exception's sidebar.
 */
const ExceptionCommentsCell = ({ data, users }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const comments = data?.comments || data?.conditionComments || [];
  const count = size(comments);

  if (!count) {
    return (
      <Flx ac g={1.4} sx={{ opacity: 0.55, minWidth: 0 }}>
        <SpeakerNotesOffOutlined className="thin" sx={{ fontSize: 14 }} />
        <Txt secondary sx={{ fontSize: 11 }}>
          No comments added
        </Txt>
      </Flx>
    );
  }

  const latest = newestComment(comments);
  const authorEmail = latest?.submittedBy || latest?.postedBy;
  const commentDate = latest?.commentDateTime || latest?.createDateTime;
  const daysAgo = isNil(commentDate) ? null : getDaysAgo(commentDate);
  const relativeAge =
    daysAgo == null
      ? "N/A"
      : daysAgo <= 0
        ? "Today"
        : daysAgo === 1
          ? "1 day ago"
          : `${daysAgo} days ago`;

  return (
    <CommentThreadPreviewTooltip
      enhanced
      placement="left-start"
      loan={loanDrilldown}
      comments={comments}
    >
      <Flx column g={0.4} sx={{ minWidth: 0, width: "100%" }}>
        <Flx ac g={1} wrap>
          <Box sx={{ position: "relative", display: "flex" }}>
            <Badge
              badgeContent={count}
              color="primary"
              sx={{
                ".MuiBadge-badge": {
                  fontSize: "8px",
                  height: "14px",
                  minWidth: "14px",
                  p: 0,
                },
              }}
            >
              <ChatBubbleOutline
                color="primary"
                className="thin"
                sx={{ fontSize: "14px" }}
              />
            </Badge>
          </Box>
          <Txt secondary sx={{ fontSize: 11, fontWeight: 600 }}>
            {getUserFullNameFromEmail(users, authorEmail)} (
            {formatDateShort(commentDate)})
          </Txt>
          <ChipItem
            value={relativeAge}
            color={daysAgo != null && daysAgo > 31 ? brown["A700"] : null}
          />
        </Flx>
        <CommentHtml htmlString={latest?.htmlString} />
      </Flx>
    </CommentThreadPreviewTooltip>
  );
};

/**
 * The comment body, rendered as HTML rather than flattened to text, so the
 * formatting the author applied in the editor (bold, italics, lists, links,
 * @mentions, line breaks) reads the same here as it does in the thread. Styles
 * mirror the read-only HtmlEditor / CommentsRenderer bubble. Shown in FULL —
 * deliberately not line-clamped like the pipeline cell.
 *
 * The HTML is authored in-app through the comment editor and is already
 * rendered raw this way elsewhere (CommentsRenderer), so this matches the
 * established pattern rather than introducing a new trust boundary.
 */
const CommentHtml = ({ htmlString }) => {
  if (isNil(htmlString) || htmlString === "") {
    return (
      <Txt secondary sx={{ fontSize: 11, fontStyle: "italic" }}>
        (no text)
      </Txt>
    );
  }
  return (
    <Box
      sx={{
        fontSize: 11,
        lineHeight: 1.6,
        minWidth: 0,
        color: panel.textBody,
        wordBreak: "break-word",
        overflowWrap: "break-word",
        "& > *:first-of-type": { mt: 0 },
        "& > *:last-child": { mb: 0 },
        "& p": { m: 0, mb: 0.5 },
        "& ul, & ol": { m: 0, mb: 0.5, pl: 2.25 },
        "& li": { mb: 0.15 },
        "& li > p": { m: 0 },
        "& strong, & b": { fontWeight: 700, color: panel.text },
        "& em, & i": { fontStyle: "italic" },
        "& code": {
          fontFamily: "monospace",
          fontSize: 10.5,
          background: panel.borderSubtle,
          borderRadius: "3px",
          px: 0.4,
        },
        "& blockquote": {
          m: 0,
          mb: 0.5,
          pl: 1,
          borderLeft: `2px solid ${panel.border}`,
          color: panel.textLabel,
        },
        "& a": {
          color: blue[700],
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
        },
        "& .wysiwyg-mention": { color: blue[700], fontWeight: 600 },
        "& img": { maxWidth: "100%", height: "auto", borderRadius: "4px" },
        "& table": { borderCollapse: "collapse", width: "100%" },
        "& td, & th": { border: `1px solid ${panel.border}`, px: 0.5 },
      }}
      dangerouslySetInnerHTML={{ __html: htmlString }}
    />
  );
};

const commentTime = (c) =>
  new Date(c?.commentDateTime || c?.createDateTime || c?.createdAt || 0).getTime() ||
  0;

const newestComment = (comments) =>
  (comments || []).reduce(
    (newest, c) => (!newest || commentTime(c) >= commentTime(newest) ? c : newest),
    null,
  );

// Comparator key: 0 when the exception has no comments so those rows sort last.
const newestCommentTime = (comments) =>
  size(comments) ? commentTime(newestComment(comments)) : 0;

const RiskBucketCell = ({ value }) =>
  value ? (
    <Chip
      size="small"
      variant="outlined"
      label={value}
      icon={<OutlinedFlagOutlined sx={{ fontSize: "11px !important" }} />}
      sx={{
        height: 20,
        fontSize: 10.5,
        background: "#f1f5f9",
        maxWidth: "100%",
        px: 0.5,
      }}
    />
  ) : (
    <Txt sx={{ fontSize: 11, color: panel.textFaint }}>—</Txt>
  );

const ExceptionCategoryCell = ({ value }) =>
  value ? (
    <Chip
      size="small"
      variant="outlined"
      label={value}
      icon={<LocalOfferOutlined sx={{ fontSize: "11px !important" }} />}
      sx={{
        height: 20,
        fontSize: 10.5,
        background: "#f1f5f9",
        maxWidth: "100%",
        px: 0.5,
      }}
    />
  ) : (
    <Txt sx={{ fontSize: 11, color: panel.textFaint }}>—</Txt>
  );

const UserCell = ({ value, users, emptyLabel = "—" }) => {
  const name = value ? getUserFullNameFromEmail(users, value) : null;
  if (!name)
    return (
      <Txt sx={{ fontSize: 11, color: panel.textFaint }}>{emptyLabel}</Txt>
    );
  return (
    // Wrap in a Box (forwards a ref) — MUI Tooltip needs a ref-able child, and
    // Flx is a plain function component that doesn't forward refs, so anchoring
    // the tooltip to it directly silently fails.
    <Tooltip title={value || ""} arrow>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}
      >
        <Avatar name={name} />
        <Txt
          sx={{
            fontSize: 11.5,
            color: panel.textStrong,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name.split(" ")[0]}
        </Txt>
      </Box>
    </Tooltip>
  );
};

const AssignedCell = ({ value, users }) => (
  <UserCell value={value} users={users} emptyLabel="Unassigned" />
);

const CreatedByCell = ({ value, users }) => (
  <UserCell value={value} users={users} emptyLabel="Unknown" />
);

const Avatar = ({ name }) => {
  const initials = useMemo(() => {
    if (!name) return "?";
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }, [name]);

  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        background: panel.avatarBg,
        color: panel.avatarFg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initials}
    </Box>
  );
};

export default LoanExceptionsTable;
